/**
 * AIHub — one Settings tab that gathers the LLM config surfaces behind a segmented control
 * (not tabs-under-tabs): Providers (the connections and keys the models run on), Models,
 * Model policy (which models may do each job) and Usage. Three of them are the same
 * `LLMSettingsContent` with a different `mode`. The active sub-view is kept in the URL as
 * `?tab=ai&sub=<providers|models|policy|usage>` so it's linkable + refreshable; the policy
 * sub still honors a `?stage=` deep-link (from AgentModelChip) since that param is
 * preserved. (`?sub=pipeline` is the old spelling; Settings redirects it.)
 *
 * A CUSTOMER sees only Providers: their own keys, which the house mapping then runs on at
 * the own-key rate. Which model runs which stage, and what it cost, is plumbing — admins
 * only (see contexts/InternalsContext for the rest of the same argument).
 */
import { useSearchParams } from 'react-router-dom';
import { SubTabs } from '../components/common/Tabs';
import LLMSettingsContent, { PROVIDER_PARAM } from './LLMSettings';
import TokenUsageReport from './TokenUsageReport';
import { subsFor, type AISub } from './aiHubSubs';

export default function AIHub({
  isAdmin,
  initialSub = 'providers',
}: {
  isAdmin: boolean;
  initialSub?: AISub;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const allowed = subsFor(isAdmin);
  const keys = allowed.map((s) => s.key);

  const raw = searchParams.get('sub');
  const wanted: AISub = keys.includes(raw as AISub) ? (raw as AISub) : initialSub;
  // A customer's default landing is Providers, whatever the URL said.
  const sub: AISub = keys.includes(wanted) ? wanted : keys[0];

  const setSub = (s: AISub) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'ai');
    if (s === 'providers') params.delete('sub'); // the landing sub needs no param
    else params.set('sub', s);
    if (s !== 'policy') params.delete('stage'); // a job deep-link only applies to the policy sub
    params.delete(PROVIDER_PARAM); // an open editor belongs to the sub being left
    setSearchParams(params, { replace: true });
  };

  return (
    <div>
      {allowed.length > 1 && (
        <SubTabs
          tabs={allowed}
          active={sub}
          onChange={setSub}
          description="Which models this account uses, which stage each one runs, and what they have cost."
        />
      )}

      {sub === 'usage' ? (
        <TokenUsageReport />
      ) : (
        <LLMSettingsContent
          mode={sub === 'policy' ? 'stages' : sub === 'providers' ? 'llm-providers' : 'providers'}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}
